import {
  collection,
  doc,
  DocumentData,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useCallback,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { db } from "../../firebase/config";
import { OrderStatus } from "../constants/OrderStatus";
import { useAuth } from "./AuthContext";
import UnexpectedError from "../unexpected-error/UnexpectedError";
import { Store } from "../type/Store";
import { X } from "lucide-react";
import { toast as sonnerToast } from "sonner";
import useTranslation from "../hooks/useTranslation";
import { applyColorMode, readColorMode, syncStoreDefaultColorMode } from "../../utils/colorMode";
import { isTauriMode } from "../../utils/environmentDetector";
import { rememberPaymentTableStatus } from "../layout/tableServiceStatus";
import { rememberServiceMode } from "../../utils/permissions/serviceMode";
import { readStoredCloudMevEnv } from "../../cloud-mev/cloudMevEnv";
import { adoptStore } from "../../utils/storeScope";
import { storeBillingSubactivity } from "../constants/BillingActivity";
import { applySavedUiScale } from "../../utils/uiScale";
import { rememberOfflineBatchLimit } from "../../utils/offlineBatchLimit";
import { setPlatformTaxes } from "../../utils/tax/itemTax";
import { readSignedInPosUserId, resolveCartOnLeft, resolveCategoryInMiddle } from "../../utils/posCartPosition";
import { ensureBuiltinPrinters } from "../../utils/printing/builtinPrinters";

interface StoreContextProps {
  setSelectedStoreId: (store: string | null) => void;
  selectedStoreId: string | null;
  clear: () => void;
  currentStore: Store | null | undefined;
  orders: [];
  sessions: [];
  websiteSettings: any | null;
  forceUpdate: () => void;
  posTheme: string;
  posCartOnLeft: boolean;
  posCategoryInMiddle: boolean;
}

const StoreContext = createContext<StoreContextProps>({
  setSelectedStoreId: () => { },
  selectedStoreId: null,
  clear: () => { },
  currentStore: null,
  orders: [],
  sessions: [],
  websiteSettings: null,
  forceUpdate: () => { },
  posTheme: "green",
  posCartOnLeft: false,
  posCategoryInMiddle: false,
});

export const useStore = () => useContext(StoreContext);

type Props = {
  children: ReactNode;
};

const pinLocalhostStoreCookie = (storeId: string | null | undefined) => {
  if (typeof document === "undefined" || !storeId) return;
  const host = window.location.hostname;
  if (host !== "localhost" && host !== "127.0.0.1") return;
  document.cookie = `selectedStore=${encodeURIComponent(storeId)}; path=/; max-age=31536000; SameSite=Lax`;
};

const storeIdFromLocation = () => {
  const hash = window.location.hash || "";
  const query = hash.includes("?") ? hash.slice(hash.indexOf("?") + 1) : window.location.search.slice(1);
  return new URLSearchParams(query).get("store")?.trim() || "";
};

const StoreContextProvider = ({ children }: Props) => {
  // CRITICAL FIX: Read selectedStoreId from localStorage IMMEDIATELY
  // Don't wait for useEffect - prevents race condition where components render before store is selected
  const [storeSelectionHeld, setStoreSelectionHeld] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(() => {
    const requested = storeIdFromLocation();
    if (requested) {
      localStorage.setItem("selectedStore", requested);
      return requested;
    }
    const stored = localStorage.getItem('selectedStore');
    console.log('Offline Debug [StoreContext] Initial selectedStoreId from localStorage:', stored);
    return stored;
  });
  const [orders, setOrders] = useState<DocumentData[] | null>(null);
  const [sessions, setSessions] = useState<DocumentData[] | null>(null);
  const [websiteSettings, setWebsiteSettings] = useState<DocumentData | null>(null);
  const { adminStores, memberStores, kdsStores, refresh } = useAuth();
  const [unexpectedError, setUnexpectedError] = useState(false);
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0);
  const [cachedPlanData, setCachedPlanData] = useState<{ [storeId: string]: any }>({});
  const translation = useTranslation();

  const [posTheme, setPosTheme] = useState<string>("green");
  const [posCartOnLeft, setPosCartOnLeft] = useState<boolean>(false);
  const [posCategoryInMiddle, setPosCategoryInMiddle] = useState<boolean>(false);

  useEffect(() => {
    const onLogout = () => {
      setStoreSelectionHeld(true);
      setSelectedStoreId(null);
      setOrders(null);
      setSessions(null);
    };
    window.addEventListener("back-office-logout", onLogout);
    return () => window.removeEventListener("back-office-logout", onLogout);
  }, []);

  useEffect(() => {
    pinLocalhostStoreCookie(selectedStoreId || localStorage.getItem("selectedStore"));
  }, [selectedStoreId]);

  useEffect(() => {
    console.log("[StoreContext Debug] StoreContextProvider mounted");
    console.warn("audio debug StoreContext mounted", {
      pathname: window.location?.pathname,
      href: window.location?.href,
    });
  }, []);

  const audioNotificationsRef = useRef<any>(null);
  const audioPlaybackRef = useRef<{
    stop: () => void;
  } | null>(null);

  const notifiedOnlineOrderIdsRef = useRef<Map<string, number>>(new Map());

  const didInitCoreOrdersNotifyRef = useRef(false);
  const didInitOnlineOrdersRef = useRef(false);
  const voiceStoreRef = useRef<any>(null);
  const didInitVoiceOrdersRef = useRef(false);
  const seenOnlineOrderIdsRef = useRef<Set<string>>(new Set());

  const guestLabel = translation.onlineGuest || translation.guest || "Guest";
  const newOnlineOrderLabel = translation.onlineNewOrderReceived || "New online order";

  // Load cached plan data when store is selected
  useEffect(() => {
    if (selectedStoreId) {
      import('../../utils/subscription/subscriptionSync').then((module) => {
        const { getCachedPlanData } = module;
        getCachedPlanData(selectedStoreId).then((cached) => {
          if (cached) {
            setCachedPlanData(prev => ({
              ...prev,
              [selectedStoreId]: cached
            }));
          }
        }).catch((error) => {
          console.warn('[StoreContext] Error loading cached plan data:', error);
        });
      });
    }
  }, [selectedStoreId]);

  // Sync POS Theme and Layout Settings
  useEffect(() => {
    const loadSettings = async () => {
      if (!selectedStoreId) return;
      try {
        const storeDB = (await import('../../utils/indexedDB/storeDB')).default;
        const theme = await storeDB.getStoreSetting(selectedStoreId, 'posTheme');
        const layout = await storeDB.getStoreSetting(selectedStoreId, 'posCartOnLeft');
        const categoryLayout = await storeDB.getStoreSetting(selectedStoreId, 'posCategoryInMiddle');
        if (theme) setPosTheme(theme);
        const storeDefault = layout !== undefined ? !!layout : false;
        const userId = readSignedInPosUserId(selectedStoreId);
        setPosCartOnLeft(resolveCartOnLeft(storeDefault, selectedStoreId, userId));
        const categoryStoreDefault = categoryLayout !== undefined ? !!categoryLayout : false;
        setPosCategoryInMiddle(resolveCategoryInMiddle(categoryStoreDefault, selectedStoreId, userId));
      } catch (err) {
        console.warn('[StoreContext] Error loading POS settings:', err);
      }
    };
    loadSettings();

    const handleThemeUpdate = (e: any) => {
      if (e.detail?.theme) setPosTheme(e.detail.theme);
    };
    const handleLayoutUpdate = (e: any) => {
      if (e.detail?.storeId && e.detail.storeId !== selectedStoreId) return;
      const userId = readSignedInPosUserId(selectedStoreId);
      if (e.detail?.posCategoryInMiddle !== undefined) {
        setPosCategoryInMiddle(e.detail.userOverride
          ? !!e.detail.posCategoryInMiddle
          : resolveCategoryInMiddle(!!e.detail.posCategoryInMiddle, selectedStoreId, userId));
      }
      if (e.detail?.posCartOnLeft === undefined) return;
      if (e.detail.userOverride) {
        setPosCartOnLeft(!!e.detail.posCartOnLeft);
        return;
      }
      setPosCartOnLeft(resolveCartOnLeft(!!e.detail.posCartOnLeft, selectedStoreId, userId));
    };

    window.addEventListener('posThemeUpdated' as any, handleThemeUpdate);
    window.addEventListener('posLayoutUpdated' as any, handleLayoutUpdate);
    return () => {
      window.removeEventListener('posThemeUpdated' as any, handleThemeUpdate);
      window.removeEventListener('posLayoutUpdated' as any, handleLayoutUpdate);
    };
  }, [selectedStoreId]);

  const stopCurrentNotificationSound = useCallback(() => {
    console.warn("audio debug stopCurrentNotificationSound");
    try {
      audioPlaybackRef.current?.stop?.();
    } catch (e) {
      // non-critical
    } finally {
      audioPlaybackRef.current = null;
    }
  }, []);

  const playOnlineOrderSound = useCallback(() => {
    const an = audioNotificationsRef.current;
    const rawSoundPath = typeof an?.soundPath === "string" ? an.soundPath : "";
    const normalizedSoundPath =
      rawSoundPath === "/assets/pos-beep.mp3" ? "/assets/sound/chime-alert-demo-309545.mp3" : rawSoundPath;
    const soundPath = normalizedSoundPath || "/assets/sound/chime-alert-demo-309545.mp3";

    const playOnNewOrder = typeof an?.playOnNewOrder === "boolean" ? an.playOnNewOrder : true;
    const repeatCountRaw = Number(an?.repeatCount);
    const repeatCount = Math.max(1, Math.min(50, Number.isFinite(repeatCountRaw) ? repeatCountRaw : 1));
    const stopOnClick = !!an?.stopOnClick;

    console.warn("audio debug playOnlineOrderSound config", {
      rawSoundPath,
      normalizedSoundPath,
      soundPath,
      playOnNewOrder,
      repeatCount,
      stopOnClick,
    });

    if (!playOnNewOrder) {
      console.warn("audio debug playOnlineOrderSound skipped: playOnNewOrder=false");
      return;
    }
    if (!soundPath) {
      console.warn("audio debug playOnlineOrderSound skipped: empty soundPath");
      return;
    }

    // Always restart from a clean slate for a new order notification
    stopCurrentNotificationSound();

    let stopped = false;
    let audio: HTMLAudioElement | null = null;
    let clickHandler: (() => void) | null = null;

    const stop = () => {
      stopped = true;
      try {
        if (audio) {
          audio.pause();
          audio.currentTime = 0;
        }
      } catch (e) {
        // non-critical
      }
      if (clickHandler) {
        window.removeEventListener("click", clickHandler);
        clickHandler = null;
      }
    };

    audioPlaybackRef.current = { stop };

    if (stopOnClick) {
      clickHandler = () => stop();
      window.addEventListener("click", clickHandler, { once: true });
    }

    const waitForEventOrTimeout = (a: HTMLAudioElement, eventName: string, timeoutMs: number) =>
      new Promise<void>((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error("timeout")), timeoutMs);
        const done = () => {
          clearTimeout(timer);
          resolve();
        };
        a.addEventListener(eventName, done, { once: true });
        a.addEventListener(
          "error",
          () => {
            clearTimeout(timer);
            reject(new Error("error"));
          },
          { once: true }
        );
      });

    (async () => {
      try {
        for (let i = 0; i < repeatCount; i++) {
          if (stopped) break;
          console.warn("audio debug playOnlineOrderSound iteration", { i: i + 1, repeatCount, soundPath });
          const currentAudio = new Audio(soundPath);
          currentAudio.preload = "auto";
          currentAudio.volume = 0.7;
          currentAudio.currentTime = 0;
          audio = currentAudio;

          // Guard so notification never blocks UI.
          try {
            await waitForEventOrTimeout(currentAudio, "loadeddata", 2500);
          } catch (e) {
            console.warn("audio debug loadeddata wait failed", e);
          }

          let playPromise: any;
          try {
            playPromise = currentAudio.play();
          } catch (e) {
            console.warn("audio debug audio.play threw", e);
            throw e;
          }
          if (playPromise && typeof playPromise.then === "function") {
            try {
              await playPromise;
            } catch (e) {
              console.warn("audio debug audio.play rejected", e);
              throw e;
            }
          }

          await waitForEventOrTimeout(currentAudio, "ended", 30000);
        }
      } catch (e) {
        console.warn("audio debug online order sound failed", e);
      } finally {
        stop();
        if (audioPlaybackRef.current?.stop === stop) {
          audioPlaybackRef.current = null;
        }
      }
    })();
  }, [stopCurrentNotificationSound]);

  const rememberOnlineOrder = useCallback((order: any) => {
    const id = order?.id ? String(order.id) : "";
    if (!id) return;
    notifiedOnlineOrderIdsRef.current.set(id, Date.now());
    if (notifiedOnlineOrderIdsRef.current.size > 500) {
      const firstKey = notifiedOnlineOrderIdsRef.current.keys().next().value;
      if (firstKey) notifiedOnlineOrderIdsRef.current.delete(firstKey);
    }
  }, []);

  const showOnlineOrderToast = useCallback((order: any) => {
    const id = order?.id ? String(order.id) : "";
    const customerName = order?.customerName || guestLabel;
    const counter = order?.counter != null ? `#${order.counter}` : "";
    const phone = order?.customerPhone || order?.phone || order?.customer?.phone || "";
    const closeLabel = translation.close || "Close";

    console.warn("audio debug showOnlineOrderToast", { id, counter, customerName });

    if (id) {
      // Dedupe toasts within the same app run (prevents re-toasting when query results shift).
      const now = Date.now();
      const last = notifiedOnlineOrderIdsRef.current.get(id);
      if (last && now - last < 1000 * 60 * 60 * 12) {
        console.warn("audio debug toast deduped", { id, counter });
        return;
      }
      rememberOnlineOrder(order);
    }

    try {
      playOnlineOrderSound();
    } catch (e) {
      // Non-critical
    }

    const title = `${newOnlineOrderLabel}${counter ? ` ${counter}` : ""}`;
    const description = phone ? `${customerName}\n${phone}` : customerName;
    sonnerToast.custom((t) => (
      <div className="flex w-[min(92vw,360px)] items-center gap-3 rounded-xl border border-white/15 bg-[#1e293b] px-3 py-2 text-white shadow-md">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-snug">{title}</p>
          {description ? (
            <p className="mt-0.5 whitespace-pre-line text-xs leading-snug text-zinc-300">{description}</p>
          ) : null}
        </div>
        <button
          type="button"
          aria-label={closeLabel}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-zinc-300 hover:bg-white/10"
          onClick={() => sonnerToast.dismiss(t)}
        >
          <X className="h-4 w-4" strokeWidth={2.25} />
        </button>
      </div>
    ), {
      id: id ? `online-order-${id}` : undefined,
      duration: 30000,
      closeButton: false,
      unstyled: true,
    });
    import("../../utils/desktop/osNotify")
      .then((mod) => mod.notifyOnlineOrder(counter || undefined))
      .catch(() => {});
  }, [guestLabel, newOnlineOrderLabel, playOnlineOrderSound, rememberOnlineOrder, translation.close]);

  const [localStationId, setLocalStationId] = useState("");

  useEffect(() => {
    if (!selectedStoreId) {
      setLocalStationId("");
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const storeDB = (await import("../../utils/indexedDB/storeDB")).default;
        await storeDB.init();
        const local = await storeDB.getStationId(selectedStoreId);
        if (!cancelled) setLocalStationId(local?.assigned === true ? String(local.stationId || "") : "");
      } catch {
        if (!cancelled) setLocalStationId("");
      }
    };
    load();
    window.addEventListener("workstation-station", load);
    return () => {
      cancelled = true;
      window.removeEventListener("workstation-station", load);
    };
  }, [selectedStoreId]);

  const currentStore = useMemo(() => {
    const store = memberStores?.find((store) => store.id === selectedStoreId) ||
      adminStores?.find((store) => store.id === selectedStoreId) ||
      kdsStores?.find((store) => store.id === selectedStoreId);

    // SAFETY NET: If AuthContext didn't load stores (offline), try masterDataCache
    if (!store && selectedStoreId) {
      console.log('Offline Debug [StoreContext] Store not found in AuthContext, checking masterDataCache...');
      // This will be handled by a separate useEffect to avoid async in useMemo
      return null;
    }

    if (!store) {
      console.log('Offline Debug [StoreContext] No store selected or found');
      return null;
    }

    // Default to Essential plan for existing stores without a plan
    const defaultPlan = store.subscriptionPlan || 'essential';
    const defaultStatus = store.subscriptionStatus || 'active';
    const defaultOrderLimit = store.orderLimit ?? 100;
    const defaultStationStartTime = store.stationStartTime || '04:00';

    // Merge cached plan data if available
    if (cachedPlanData[selectedStoreId || '']) {
      const cached = cachedPlanData[selectedStoreId || ''];
      console.log('Offline Debug [StoreContext] Using cached plan data for store:', selectedStoreId);
      return {
        ...store,
        stationId: localStationId,
        subscriptionPlan: store.subscriptionPlan || cached.subscriptionPlan || defaultPlan,
        subscriptionStatus: store.subscriptionStatus || cached.subscriptionStatus || defaultStatus,
        orderLimit: store.orderLimit ?? cached.orderLimit ?? defaultOrderLimit,
        stationStartTime: store.stationStartTime || defaultStationStartTime,
      };
    }

    // Return store with defaults if plan is missing
    const result = {
      ...store,
      stationId: localStationId,
      subscriptionPlan: defaultPlan,
      subscriptionStatus: defaultStatus,
      orderLimit: defaultOrderLimit,
      stationStartTime: defaultStationStartTime,
    };
    console.log("Offline Debug [StoreContext] Computed currentStore:", result.id, result.stationStartTime);
    return result;
  }, [adminStores, memberStores, kdsStores, selectedStoreId, forceUpdate, cachedPlanData, refresh, localStationId]);

  useEffect(() => {
    const mode = currentStore?.colorMode;
    if (mode !== "light" && mode !== "dark") return;
    const active = syncStoreDefaultColorMode(mode);
    applyColorMode(active, window.location.hash.replace(/^#/, ""));
  }, [currentStore?.colorMode]);

  // SAFETY NET: Fallback to masterDataCache if currentStore is null
  const [fallbackStore, setFallbackStore] = useState<Store | null>(null);

  useEffect(() => {
    if (!currentStore && selectedStoreId && !fallbackStore) {
      console.log('Offline Debug [StoreContext] Attempting fallback to masterDataCache...');
      (async () => {
        try {
          const masterDataCache = (await import('../../utils/offlineStorage/masterDataCache')).default;
          const cached = await masterDataCache.get('stores', selectedStoreId);
          if (cached) {
            console.log('Offline Debug [StoreContext] ✓ Loaded store from masterDataCache fallback:', selectedStoreId);
            setFallbackStore(cached as Store);
          } else {
            console.warn('Offline Debug [StoreContext] ✗ Store not found in masterDataCache:', selectedStoreId);
          }
        } catch (error) {
          console.error('Offline Debug [StoreContext] Error loading from masterDataCache:', error);
        }
      })();
    }
  }, [currentStore, selectedStoreId, fallbackStore]);

  const [voiceRouting, setVoiceRouting] = useState<any>(null);

  useEffect(() => {
    if (!selectedStoreId) {
      setVoiceRouting(null);
      return undefined;
    }
    return onSnapshot(doc(db, "stores", selectedStoreId), (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      setVoiceRouting({
        voiceAnnouncement: data.voiceAnnouncement || null,
        kdsProfiles: data.kdsProfiles || {},
        kdsUsers: Array.isArray(data.kdsUsers) ? data.kdsUsers : [],
        roleAssignments: Array.isArray(data.roleAssignments) ? data.roleAssignments : [],
        approvalPins: Array.isArray(data.approvalPins) ? data.approvalPins : [],
        emailReceiptAllowed: data.emailReceiptAllowed === true,
        customerScreenActive: data.customerScreenActive === true,
        customerScreenAlwaysOn: data.customerScreenAlwaysOn === true,
        customerScreenColorMode: data.customerScreenColorMode === "dark" ? "dark" : "light",
        receiptDelivery: data.receiptDelivery || null,
        receiptOwnerEmails: Array.isArray(data.receiptOwnerEmails) ? data.receiptOwnerEmails : [],
        subscriptionStatus: data.subscriptionStatus || "active",
        serviceMode: data.serviceMode === "assigned" ? "assigned" : "shared",
      });
    });
  }, [selectedStoreId]);

  // Sync state to currentStore object for components that rely on it
  useEffect(() => {
    return onSnapshot(doc(db, "systemConfig", "taxes"), (snap) => {
      setPlatformTaxes(snap.exists() ? snap.data()?.taxes : null, snap.exists() ? snap.data() : null);
    }, () => {
      setPlatformTaxes(null);
    });
  }, []);

  const currentStoreWithSettings = useMemo(() => {
    const storeToUse = currentStore || fallbackStore;
    if (!storeToUse) {
      console.log('Offline Debug [StoreContext] No store available (neither AuthContext nor fallback)');
      return null;
    }
    return {
      ...storeToUse,
      stationId: localStationId,
      ...(voiceRouting || {}),
      posTheme: posTheme,
      posCartOnLeft: posCartOnLeft,
      posCategoryInMiddle: posCategoryInMiddle,
    };
  }, [currentStore, fallbackStore, posTheme, posCartOnLeft, posCategoryInMiddle, voiceRouting, localStationId]);

  voiceStoreRef.current = currentStoreWithSettings;

  useEffect(() => {
    if (!selectedStoreId) return;
    ensureBuiltinPrinters(selectedStoreId).catch(() => {});
  }, [selectedStoreId]);

  useEffect(() => {
    if (!selectedStoreId) return;
    adoptStore(selectedStoreId);
    const envirn = readStoredCloudMevEnv(selectedStoreId);
    if (envirn) {
      try { localStorage.setItem("envirn", envirn); } catch { /* The signed-in store keeps its own environment key. */ }
    }
    applySavedUiScale();
    applyColorMode(readColorMode(), window.location.hash.replace(/^#/, ""));
  }, [selectedStoreId]);

  useEffect(() => {
    const storeId = currentStoreWithSettings?.id;
    if (!storeId) return;
    try {
      localStorage.setItem(`billingSubactivity_${storeId}`, storeBillingSubactivity(currentStoreWithSettings));
    } catch {
      // A bill still defaults to restaurant when this value cannot be stored.
    }
  }, [currentStoreWithSettings?.id, currentStoreWithSettings?.billingSubactivity]);

  useEffect(() => {
    const storeId = currentStoreWithSettings?.id;
    const mode = currentStoreWithSettings?.tableStatusAfterPayment;
    if (!storeId || (mode !== "available" && mode !== "dirty")) return;
    rememberPaymentTableStatus(storeId, mode);
  }, [currentStoreWithSettings?.id, currentStoreWithSettings?.tableStatusAfterPayment]);

  useEffect(() => {
    const storeId = currentStoreWithSettings?.id;
    if (!storeId) return;
    rememberServiceMode(storeId, currentStoreWithSettings?.serviceMode);
  }, [currentStoreWithSettings?.id, currentStoreWithSettings?.serviceMode]);

  useEffect(() => {
    const storeId = currentStoreWithSettings?.id;
    if (!storeId) return;
    rememberOfflineBatchLimit(storeId, currentStoreWithSettings?.offlineBatchSizeKb);
  }, [currentStoreWithSettings?.id, currentStoreWithSettings?.offlineBatchSizeKb]);

  const clear = () => {
    setSelectedStoreId(null);
    setOrders(null);
    setSessions(null);
  };

  useEffect(() => {
    if (storeSelectionHeld) return;
    const saved = localStorage.getItem("selectedStore") || "";
    // A refresh keeps the last store. The first store in a list is only for a sign-in that has none yet.
    if (!saved) {
      const fallback =
        (isTauriMode() && adminStores && adminStores.length > 0 && adminStores[0].id) ||
        (memberStores && memberStores.length > 0 && memberStores[0].id) ||
        (adminStores && adminStores.length > 0 && adminStores[0].id) ||
        (kdsStores && kdsStores.length > 0 && kdsStores[0].id) ||
        "";
      if (!fallback) return;
      localStorage.setItem("selectedStore", fallback);
      setSelectedStoreId(fallback);
      return;
    }
    if (!selectedStoreId) {
      setSelectedStoreId(saved);
      return;
    }
    if (saved !== selectedStoreId) {
      localStorage.setItem("selectedStore", selectedStoreId);
    }
  }, [selectedStoreId, adminStores, memberStores, kdsStores, storeSelectionHeld]);

  useEffect(() => {
    // Sync sound setting to soundService
    if (currentStore) {
      import('../../utils/soundService').then((module) => {
        const soundService = module.default;
        // Default to true if not explicitly set to false
        const soundEnabled = (currentStore as any).soundEnabled !== false;
        soundService.setSoundEnabled(soundEnabled);
      });

      // Sync plan data to IndexedDB for offline access
      if (currentStore.id) {
        import('../../utils/subscription/subscriptionSync').then((module) => {
          const { syncPlanDataToIndexedDB } = module;
          syncPlanDataToIndexedDB(currentStore.id, {
            subscriptionPlan: currentStore.subscriptionPlan,
            subscriptionStatus: currentStore.subscriptionStatus,
            orderLimit: currentStore.orderLimit,
          }).catch((error) => {
            console.warn('[StoreContext] Error syncing plan data to IndexedDB:', error);
          });
        });
      }
    }
  }, [currentStore]);

  useEffect(() => {
    // load store data
    if (selectedStoreId) {
      console.warn("audio debug StoreContext selectedStoreId", { selectedStoreId });
      // Subscribe to website audio notification settings for this store
      const unsubscribeWebsiteSettings = onSnapshot(
        doc(db, "websiteSettings", selectedStoreId),
        (snap) => {
          const data: any = snap.exists() ? snap.data() : null;
          setWebsiteSettings(data);
          audioNotificationsRef.current = data?.system?.audioNotifications || null;
          console.warn("audio debug websiteSettings updated", {
            storeId: selectedStoreId,
            audioNotifications: audioNotificationsRef.current,
          });
        },
        (e) => {
          console.warn("[StoreContext] Error in websiteSettings listener:", e);
          audioNotificationsRef.current = null;
        }
      );

      // Reset per-store online-order notifications
      didInitCoreOrdersNotifyRef.current = false;
      didInitOnlineOrdersRef.current = false;
      didInitVoiceOrdersRef.current = false;
      seenOnlineOrderIdsRef.current = new Set();

      // OFFLINE-FIRST: Load orders from IndexedDB cache first (instant, always available)
      const loadOrdersFromIndexedDB = async () => {
        try {
          const offlineDB = (await import('../../utils/indexedDB/offlineDB')).default;
          await offlineDB.init();
          const cachedOrders = await offlineDB.getCollection('orders');
          const storeOrders = (cachedOrders || []).filter((o: any) => o?.storeId === selectedStoreId);
          if (storeOrders.length > 0) {
            setOrders(storeOrders);
            console.log(`[StoreContext Debug] Loaded ${storeOrders.length} orders from IndexedDB cache`);
          }
        } catch (error) {
          console.warn('[StoreContext] Error loading orders from IndexedDB cache:', error);
        }
      };

      // OFFLINE-FIRST: Load sessions from IndexedDB first (instant, always available)
      const loadSessionsFromIndexedDB = async () => {
        try {
          const cartDB = (await import('../../utils/indexedDB/cartDB')).default;
          await cartDB.init();
          const indexedDBSessions = await cartDB.getAllSessions(selectedStoreId);

          if (indexedDBSessions && indexedDBSessions.length > 0) {
            // Convert IndexedDB sessions to format expected by StoreContext
            const formattedSessions = indexedDBSessions.map((s: any) => ({
              ...s,
              id: s.id || s.sessionId,
            }));
            setSessions(formattedSessions);
            console.log(`[StoreContext Debug] Loaded ${formattedSessions.length} sessions from IndexedDB`);
          }
        } catch (error) {
          console.warn('[StoreContext] Error loading sessions from IndexedDB:', error);
        }
      };

      // Load from IndexedDB immediately (offline-first!)
      loadOrdersFromIndexedDB();
      loadSessionsFromIndexedDB();

      const qOrders = query(
        collection(db, "orders"),
        where("storeId", "==", selectedStoreId),
        where("status", "in", [
          OrderStatus.SUBMITTED,
          OrderStatus.PAID,
        ]),
        orderBy("created", "desc"),
        limit(100)
      );

      const qOnlineOrders = query(
        collection(db, "orders"),
        where("storeId", "==", selectedStoreId),
        where("channel", "==", "ONLINE"),
        orderBy("created", "desc"),
        limit(200)
      );

      const mergeOrders = (a: DocumentData[], b: DocumentData[]) => {
        const map = new Map<string, DocumentData>();
        // Core orders are authoritative. Only use onlineOrders snapshot to add extra docs
        // that aren't present in core orders (qOnlineOrders can be stale / unreliable).
        (a || []).forEach((o: any) => {
          if (o?.id) map.set(o.id, o);
        });
        (b || []).forEach((o: any) => {
          if (o?.id && !map.has(o.id)) map.set(o.id, o);
        });

        return Array.from(map.values()).sort((x: any, y: any) => {
          const xd = (x?.created?.toDate ? x.created.toDate() : x?.created) || 0;
          const yd = (y?.created?.toDate ? y.created.toDate() : y?.created) || 0;
          const xt = xd instanceof Date ? xd.getTime() : Number(xd) || 0;
          const yt = yd instanceof Date ? yd.getTime() : Number(yd) || 0;
          return yt - xt;
        });
      };

      let lastCoreOrders: DocumentData[] = [];
      let lastOnlineOrders: DocumentData[] = [];

      const unsubscribeOrders = onSnapshot(
        qOrders,
        (querySnapshot) => {
            console.warn("audio debug core orders snapshot", {
              storeId: selectedStoreId,
              size: querySnapshot.size,
              fromCache: querySnapshot.metadata.fromCache,
              hasPendingWrites: querySnapshot.metadata.hasPendingWrites,
              changeCount: querySnapshot.docChanges().length,
            });

            // Fallback: detect newly created ONLINE orders via the core orders query.
            // (Some environments appear not to deliver added events to qOnlineOrders reliably.)
            const changes = querySnapshot.docChanges();
            const addedOnlineOrders = changes
              .filter((c) => c.type === "added")
              .map((c) => {
                const data: any = c.doc.data();
                data.id = c.doc.id;
                return data;
              })
              .filter((o: any) => (o?.channel || "").toString().toUpperCase() === "ONLINE")
              .filter((o: any) => String(o?.onlineStatus || "UNCONFIRMED").toUpperCase() === "UNCONFIRMED");

            if (!didInitCoreOrdersNotifyRef.current) {
              const cacheEmpty = querySnapshot.metadata.fromCache && querySnapshot.size === 0;
              addedOnlineOrders.forEach((o: any) => rememberOnlineOrder(o));
              if (!cacheEmpty) didInitCoreOrdersNotifyRef.current = true;
            } else if (addedOnlineOrders.length > 0) {
              console.warn(
                "audio debug detected new ONLINE orders (from core orders)",
                addedOnlineOrders.map((o: any) => ({ id: o?.id, counter: o?.counter, onlineStatus: o?.onlineStatus }))
              );
              addedOnlineOrders.forEach((o: any) => {
                showOnlineOrderToast(o);
              });
            }

            const voiceAdded = changes
              .filter((c) => c.type === "added")
              .map((c) => {
                const data: any = c.doc.data();
                data.id = c.doc.id;
                return data;
              });
            if (!didInitVoiceOrdersRef.current) {
              didInitVoiceOrdersRef.current = true;
              import("../../utils/voice/announceOrder").then(({ rememberOrderIds }) => {
                rememberOrderIds(voiceAdded.map((order: any) => order.id));
              }).catch(() => {});
            } else {
              import("../../utils/voice/announceOrder").then(({ announceNewOrder }) => {
                voiceAdded.forEach((order: any) => announceNewOrder(order, voiceStoreRef.current));
              }).catch(() => {});
            }

            const orders: DocumentData[] = [];

            querySnapshot.forEach((doc) => {
              const order = doc.data();
              order.id = doc.id;
              orders.push(order);
            });
            lastCoreOrders = orders;
            setOrders(mergeOrders(lastCoreOrders, lastOnlineOrders));

            if (!querySnapshot.metadata.hasPendingWrites) {
              import('../../utils/pos/finishedOrders').then(({ persistFinishedOrderLocally, isPaidOrderStatus, pruneFinishedOrders }) => {
                orders.forEach((order: any) => {
                  if (!order?.id || !isPaidOrderStatus(order.status)) return;
                  persistFinishedOrderLocally(order.id, order).catch(() => { });
                });
                pruneFinishedOrders({
                  id: selectedStoreId,
                  stationStartTime: currentStore?.stationStartTime,
                  offlineReturnMaxDays: currentStore?.offlineReturnMaxDays,
                  stationId: currentStore?.stationId,
                }, currentStore?.stationId).catch(() => { });
              }).catch(() => { });
            }
        },
        (e) => {
          // Don't clear store on error - might be offline
          console.warn('[StoreContext] Error in orders listener:', e);
        }
      );

      const unsubscribeOnlineOrders = onSnapshot(
        qOnlineOrders,
        (querySnapshot) => {
          const orders: DocumentData[] = [];

          querySnapshot.forEach((doc) => {
            const order = doc.data();
            order.id = doc.id;
            orders.push(order);
          });

          console.warn("audio debug online orders snapshot", {
            storeId: selectedStoreId,
            size: querySnapshot.size,
            fromCache: querySnapshot.metadata.fromCache,
            hasPendingWrites: querySnapshot.metadata.hasPendingWrites,
            changeCount: querySnapshot.docChanges().length,
          });

          // Global ONLINE order notification using docChanges() to reliably detect newly created docs
          const changes = querySnapshot.docChanges();
          if (!didInitOnlineOrdersRef.current) {
            const cacheEmpty = querySnapshot.metadata.fromCache && querySnapshot.size === 0;
            const baseline = changes
              .filter((c) => c.type === "added")
              .map((c) => {
                const data: any = c.doc.data();
                data.id = c.doc.id;
                return data;
              })
              .filter((o: any) => String(o?.onlineStatus || "UNCONFIRMED").toUpperCase() === "UNCONFIRMED");
            baseline.forEach((o: any) => rememberOnlineOrder(o));
            if (!cacheEmpty) didInitOnlineOrdersRef.current = true;
            console.warn("audio debug online orders listener init", {
              storeId: selectedStoreId,
              initialCount: orders.length,
              changeCount: changes.length,
              fromCache: querySnapshot.metadata.fromCache,
            });
          } else {
            const addedDocs = changes
              .filter((c) => c.type === "added")
              .map((c) => {
                const data: any = c.doc.data();
                data.id = c.doc.id;
                return data;
              });

            const newOnes = (addedDocs || []).filter((o: any) => {
              return String(o?.onlineStatus || "UNCONFIRMED").toUpperCase() === "UNCONFIRMED";
            });

            if (newOnes.length > 0) {
              console.warn(
                "audio debug detected new ONLINE orders",
                newOnes.map((o: any) => ({ id: o?.id, counter: o?.counter, onlineStatus: o?.onlineStatus }))
              );
            }

            newOnes.forEach((o: any) => {
              showOnlineOrderToast(o);
            });
            import("../../utils/voice/announceOrder").then(({ announceNewOrder }) => {
              newOnes.forEach((order: any) => announceNewOrder(order, voiceStoreRef.current));
            }).catch(() => {});
          }

          lastOnlineOrders = orders;
          setOrders(mergeOrders(lastCoreOrders, lastOnlineOrders));
        },
        (e) => {
          console.warn('[StoreContext] Error in online orders listener:', e);
        }
      );
      const qSessions = query(
        collection(db, "sessions"),
        where("storeId", "==", selectedStoreId),
        orderBy("created", "desc"),
        limit(100)
      );
      const unsubscribeSessions = onSnapshot(
        qSessions,
        async (querySnapshot) => {
          const firestoreSessions: DocumentData[] = [];
          querySnapshot.forEach((doc) => {
            const session = doc.data();
            session.id = doc.id;
            firestoreSessions.push(session);
          });

          // Merge Firestore sessions with IndexedDB sessions (offline-first)
          // IndexedDB has priority - it's the source of truth for local operations
          try {
            const cartDB = (await import('../../utils/indexedDB/cartDB')).default;
            await cartDB.init();
            const indexedDBSessions = await cartDB.getAllSessions(selectedStoreId);

            const getSessionKey = (s: any) => {
              const tableName = s?.tableName;
              if (typeof tableName === 'string' && (tableName.startsWith('TW') || tableName.startsWith('DL') || tableName.startsWith('CS'))) {
                return tableName;
              }
              return s?.id || s?.sessionId;
            };

            // Create map of IndexedDB sessions by ID
            const indexedDBMap = new Map();
            indexedDBSessions.forEach((s: any) => {
              const key = getSessionKey(s);
              if (key) indexedDBMap.set(key, s);
            });

            const firestoreByKey = new Map();
            firestoreSessions.forEach((fsSession) => {
              const key = getSessionKey(fsSession);
              if (key) firestoreByKey.set(key, fsSession);
            });

            // Merge: IndexedDB sessions first, then add Firestore sessions not in IndexedDB
            const mergedSessions: DocumentData[] = [];

            // Add all IndexedDB sessions (offline-first priority)
            indexedDBMap.forEach((session) => {
              const key = getSessionKey(session);
              const remote = firestoreByKey.get(key);
              const localSequence = Number(session.lanSequence || 0);
              const remoteSequence = Number(remote?.lanSequence || 0);
              const remoteNewer = Boolean(remote) && remoteSequence > localSequence;
            mergedSessions.push({
              ...session,
              id: key,
              tableName: remote?.tableName || session.tableName,
              lanSequence: localSequence,
              tableLocked: remoteNewer ? remote.tableLocked === true : session.tableLocked === true,
                tableLockedBy: remoteNewer ? (remote.tableLockedBy || "") : (session.tableLockedBy || ""),
                tableLockedById: remoteNewer ? (remote.tableLockedById || "") : (session.tableLockedById || ""),
                tableLockedUntil: remoteNewer
                  ? (remote.tableLockedUntil ?? null)
                  : (session.tableLockedUntil ?? null),
              });
            });

            // Add Firestore sessions that aren't in IndexedDB (for multi-device sync)
            firestoreSessions.forEach((fsSession) => {
              const key = getSessionKey(fsSession);
              const sameTable = fsSession.tableName
                ? indexedDBSessions.find((session: any) => session.tableName === fsSession.tableName)
                : null;
              if (sameTable && Number(sameTable.lanSequence || 0) >= Number(fsSession.lanSequence || 0)) return;
              if (key && !indexedDBMap.has(key)) {
                mergedSessions.push({
                  ...fsSession,
                  id: key,
                });
                // Also save to IndexedDB for offline access
                cartDB.saveSessionObject(fsSession).catch(err =>
                  console.warn('[StoreContext] Error saving Firestore session to IndexedDB:', err)
                );
              }
            });

            setSessions(mergedSessions);
            console.log(`[StoreContext] Merged ${mergedSessions.length} sessions (${indexedDBSessions.length} from IndexedDB, ${firestoreSessions.length} from Firestore)`);
          } catch (error) {
            // Fallback to Firestore only if IndexedDB merge fails
            console.warn('[StoreContext] Error merging sessions, using Firestore only:', error);
            setSessions(firestoreSessions);
          }
        },
        (e) => {
          // Don't clear store on error - might be offline, use IndexedDB sessions
          console.warn('[StoreContext] Error in sessions listener:', e);
          // Keep IndexedDB sessions on Firestore error
        }
      );

      return () => {
        unsubscribeWebsiteSettings();
        unsubscribeOrders();
        unsubscribeOnlineOrders();
        unsubscribeSessions();
        stopCurrentNotificationSound();
      };
    }
  }, [rememberOnlineOrder, selectedStoreId, showOnlineOrderToast, stopCurrentNotificationSound]);

  useEffect(() => {
    if (!selectedStoreId) return undefined;
    const reload = async () => {
      try {
        const cartDB = (await import("../../utils/indexedDB/cartDB")).default;
        await cartDB.init();
        const local = await cartDB.getAllSessions(selectedStoreId);
        setSessions((current) => {
          const map = new Map((current || []).map((session: any) => [session.id || session.sessionId, session]));
          local.forEach((session: any) => {
            const key = session.id || session.sessionId;
            if (!key) return;
            const existing = map.get(key);
            if (existing && Number(existing.lanSequence || 0) > Number(session.lanSequence || 0)) return;
            map.set(key, { ...existing, ...session, id: key });
          });
          return Array.from(map.values());
        });
      } catch (error) {
        console.warn("[StoreContext] Workstation sync session reload failed:", error);
      }
    };
    window.addEventListener("lan-sync-applied", reload);
    return () => window.removeEventListener("lan-sync-applied", reload);
  }, [selectedStoreId]);

  const value: any = {
    setSelectedStoreId,
    selectedStoreId,
    clear,
    currentStore: currentStoreWithSettings,
    orders,
    sessions,
    websiteSettings,
    forceUpdate,
    posTheme,
    posCartOnLeft,
    posCategoryInMiddle,
  };

  return (
    <StoreContext.Provider value={value}>
      {!unexpectedError ? children : <UnexpectedError />}
    </StoreContext.Provider>
  );
};

export default StoreContextProvider;
